import {
  api,
  type ObservedResolution,
  type ResolutionCaseFull,
  type ResolutionOutcome,
  type ResolutionScore,
} from '../lib/api.ts'
import { RunMetric, RunModal } from './RunModal.tsx'
import { useGoldenRun } from '../lib/useGoldenRun.ts'

/**
 * Running a resolution case, in the same shell the reading set uses.
 *
 * The same act on the other answer key: one button, one wait, one table of
 * expected beside produced. What differs is what a row *is* -- a reading
 * compares fields, this compares outcomes -- and that resolution can say why
 * it concluded what it did, which a field comparison has no equivalent of. So
 * the reasoning is a column rather than a different screen.
 *
 * Kept, unlike the reading run: `POST /:caseId/run` files a row with the
 * expectations as they stood, because a case is editable and grading an old
 * run against today's answers would let the history rewrite itself.
 */
const OUTCOME: Readonly<Record<ResolutionOutcome, string>> = {
  consistent: 'Concorda',
  suggest_correction: 'Sugere correção',
  unresolved: 'Não conclui',
}

/** Red only where we accused somebody wrongly. Everything else is neutral. */
function rowTone(grade: string | undefined): string {
  if (grade === 'false_accusation') return 'bg-fill-error-weak/40'
  if (grade === 'wrong_rival' || grade === 'missed_catch') return 'bg-fill-warning-weak/40'
  return ''
}

export function ResolutionCaseRun({
  kase,
  onClose,
}: {
  kase: ResolutionCaseFull
  onClose: () => void
}) {
  /*
   * Started, then followed. See the reading set's twin: a run that takes tens
   * of seconds cannot be awaited inside one request, because the socket is
   * closed underneath the browser at ten.
   */
  const { result, error, busy } = useGoldenRun<{
    score: ResolutionScore
    observed: readonly ObservedResolution[]
  }>(
    () => api.runResolutionCase(kase.caseId),
    async (runId) => {
      const run = await api.resolutionRunStatus(kase.caseId, runId)
      return {
        status: run.status,
        result: run.score === null ? undefined : { score: run.score, observed: run.observed ?? [] },
        error: run.error,
      }
    },
    kase.caseId,
  )

  const score = result?.score
  const observed = result?.observed ?? []
  const byTerm = new Map(observed.map((o) => [o.prescribed, o]))

  return (
    <RunModal
      title="Comparação do caso"
      caseId={kase.caseId}
      busy={busy}
      error={error}
      metrics={
        score === undefined ? undefined : (
          <div className="rounded-base border-stroke-weak flex flex-wrap gap-6 border p-3">
            <RunMetric label="Acertos" value={`${score.correct}/${score.total}`} />
            {/*
              First and loudest. A change that improves accuracy while raising
              this is a regression, so it must never read as a detail.
            */}
            <RunMetric
              label="Acusações falsas"
              value={String(score.falseAccusations)}
              tone={score.falseAccusations > 0 ? 'error' : 'ok'}
            />
            <RunMetric
              label="Deixou passar"
              value={String(score.missedCatches)}
              tone={score.missedCatches > 0 ? 'warn' : 'ok'}
            />
            <RunMetric
              label="Correção errada"
              value={String(score.wrongRivals)}
              tone={score.wrongRivals > 0 ? 'warn' : 'ok'}
            />
          </div>
        )
      }
      onClose={onClose}
    >
      {score !== undefined && (
        <>
          <div className="rounded-base border-stroke-weak w-full overflow-x-auto border">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  {['Ingrediente', 'Digitado', 'Esperado', 'Obtido', 'Motivo', 'Por quê'].map(
                    (h) => (
                      <th
                        key={h}
                        scope="col"
                        className="border-stroke-weak bg-background-sunken text-tTiny text-text-weak border-b px-3 py-2 text-left font-semibold uppercase"
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {kase.ingredients.map((ingredient, index) => {
                  const actual = byTerm.get(ingredient.prescribed)
                  const row = score.rows.find((r) => r.prescribed === ingredient.prescribed)
                  const selected = kase.catalogue.find((p) => p.erpCode === ingredient.selected)
                  return (
                    <tr key={`${ingredient.prescribed}-${index}`} className={rowTone(row?.grade)}>
                      <td className="border-stroke-weak text-tP text-text-strong border-b px-3 py-2">
                        {ingredient.prescribed}
                        {ingredient.dose !== null && (
                          <span className="text-tTiny text-text-weak ml-2">{ingredient.dose}</span>
                        )}
                      </td>
                      <td className="border-stroke-weak text-tP text-text-weak border-b px-3 py-2">
                        <span className="font-mono">{ingredient.selected}</span>
                        {selected !== undefined && (
                          <span className="text-tTiny text-text-weak ml-2">{selected.name}</span>
                        )}
                      </td>
                      <td className="border-stroke-weak text-tP text-text-weak border-b px-3 py-2">
                        {OUTCOME[ingredient.expect]}
                        {ingredient.expectRival !== null && (
                          <span className="text-tTiny text-text-weak ml-1 font-mono">
                            → {ingredient.expectRival}
                          </span>
                        )}
                      </td>
                      {/* The two sides side by side, as the reading run has
                          them: what the case asserts, then what came back. */}
                      <td className="border-stroke-weak text-tP border-b px-3 py-2">
                        {actual === undefined ? (
                          <span className="text-text-weak">—</span>
                        ) : (
                          <>
                            <span className="text-text-strong">{OUTCOME[actual.outcome]}</span>
                            {actual.rivalCode !== undefined && (
                              <span className="text-tTiny text-text-weak ml-1 font-mono">
                                → {actual.rivalCode}
                              </span>
                            )}
                          </>
                        )}
                      </td>
                      {/*
                        `decide`'s own words, never the model's prose: the
                        column that separates "the resolver was wrong" from
                        "retrieval never showed it the product".
                      */}
                      <td className="border-stroke-weak text-tTiny text-text-weak max-w-prose border-b px-3 py-2">
                        {actual?.reason ?? '—'}
                      </td>
                      <td className="border-stroke-weak text-tTiny text-text-weak max-w-prose border-b px-3 py-2">
                        {ingredient.why}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {observed.length > 0 && <Candidates observed={observed} />}
        </>
      )}
    </RunModal>
  )
}

/**
 * What the resolver was shown and what it made of it.
 *
 * The diagnosis lives here rather than in the outcome. A case can fail because
 * retrieval never returned the selected product, because the model scored it
 * badly, or because a threshold is wrong — and those look identical from the
 * outcome column alone.
 */
function Candidates({ observed }: { observed: readonly ObservedResolution[] }) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-tPSemi text-text-strong">O que o resolvedor viu</h2>
      {observed.map((item, index) => (
        <div
          key={`${item.prescribed}-${index}`}
          className="rounded-base border-stroke-weak bg-background-raised flex flex-col gap-2 border p-3"
        >
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="text-tPSemi text-text-strong">{item.prescribed}</span>
            {item.uncertain === true && (
              <span className="bg-fill-warning-weak text-text-warning rounded-base text-tTiny px-1.5 py-0.5">
                declarou incerteza
              </span>
            )}
            {item.interpretation !== undefined && (
              <span className="text-tSmall text-text-weak">“{item.interpretation}”</span>
            )}
          </div>
          <ul className="flex flex-col gap-1">
            {(item.candidates ?? []).map((candidate) => (
              <li
                key={candidate.erpCode}
                className="text-tSmall flex flex-wrap items-baseline gap-2"
              >
                <span className="text-text-strong font-mono">{candidate.erpCode}</span>
                <span className="text-text-weak">{candidate.productName}</span>
                <span className="text-text-weak rounded-base bg-fill-weak text-tTiny px-1.5 py-0.5">
                  {candidate.layer}
                  {candidate.matchedTerm !== '' && candidate.matchedTerm !== candidate.productName
                    ? `: “${candidate.matchedTerm}”`
                    : ''}
                </span>
                {candidate.score !== undefined && (
                  <span className="text-text-strong tabular-nums">
                    {candidate.score.toFixed(2)}
                  </span>
                )}
                {candidate.why !== undefined && (
                  <span className="text-text-weak">{candidate.why}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  )
}
